import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import Button from "./Button";
import "./Modal.scss";
import { X } from "lucide-react";

interface Props {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  maxWidth?: number | string;
  panelClassName?: string;
  panelStyle?: React.CSSProperties;
  headerStyle?: React.CSSProperties;
  bodyClassName?: string;
  showTopCancel?: boolean;
  overlayClassName?: string;
  hideFooter?: boolean;
  footer?: React.ReactNode;
  onDelete?: () => void;
  deleteLabel?: string;
  onSave?: () => void | Promise<unknown>;
  saveLabel?: string;
  saveType?: "button" | "submit";
  saveFormId?: string;
  saveDisabled?: boolean;
}

const Modal: React.FC<Props> = ({
  title,
  onClose,
  children,
  maxWidth = 480,
  panelClassName,
  panelStyle,
  headerStyle,
  bodyClassName,
  showTopCancel = true,
  overlayClassName,
  hideFooter = false,
  footer,
  onDelete,
  deleteLabel = "Delete",
  onSave,
  saveLabel = "Save",
  saveType = "button",
  saveFormId,
  saveDisabled = false,
}) => {
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const hasDefaultFooterActions = Boolean(onDelete || onSave || saveFormId);

  const runSave = useCallback(async () => {
    if (!onSave || savingRef.current || saveDisabled) return;
    savingRef.current = true;
    setSaving(true);
    try {
      await onSave();
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [onSave, saveDisabled]);

  useEffect(() => {
    if (!saveFormId || !onSave) return undefined;
    const form = document.getElementById(saveFormId);
    if (!form) return undefined;
    const handleSubmit = (event: Event) => {
      event.preventDefault();
      void runSave();
    };
    form.addEventListener("submit", handleSubmit);
    return () => form.removeEventListener("submit", handleSubmit);
  }, [saveFormId, onSave, runSave]);

  const content = (
    <div
      className={`modal-overlay${overlayClassName ? ` ${overlayClassName}` : ''}`}
      data-testid="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className={`modal-panel${panelClassName ? ` ${panelClassName}` : ""}`}
        style={{
          maxWidth: typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth,
          ...panelStyle,
        }}
      >
        <div className="modal-panel__header" style={headerStyle}>
          <span className="modal-panel__title">{title}</span>
          {showTopCancel && (
            <button
              className="modal-panel__close"
              onClick={onClose}
              type="button"
            >
              <X size={12} />
            </button>
          )}
        </div>
        <div
          className={`modal-panel__body${bodyClassName ? ` ${bodyClassName}` : ""}`}
        >
          {children}
        </div>
        {!hideFooter && (footer || hasDefaultFooterActions) && (
          <div className="modal-panel__footer">
            {footer ?? (
              <>
                {onDelete && (
                  <Button
                    type="button"
                    variant="danger"
                    size="md"
                    marginRightAuto
                    onClick={onDelete}
                    disabled={saving}
                  >
                    {deleteLabel}
                  </Button>
                )}
                {saveFormId ? (
                  <Button
                    type={saveType}
                    variant="primary"
                    size="md"
                    form={saveFormId}
                    loading={saving}
                    disabled={saveDisabled}
                  >
                    {saveLabel}
                  </Button>
                ) : onSave ? (
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    onClick={() => void runSave()}
                    loading={saving}
                    disabled={saveDisabled}
                  >
                    {saveLabel}
                  </Button>
                ) : null}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
};

export default Modal;
